/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import { injectIntl } from 'react-intl';
import { useField } from 'formik';

import Worker from './jsonata.worker';

import { MonacoEditorFormik } from 'Common';

const propTypes = {
  fieldId: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
  schemaBindings: PropTypes.shape({
    event: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    state: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    instance: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  }).isRequired,
  isLoading: PropTypes.bool,
};
const defaultProps = {
  readOnly: false,
  isLoading: false,
};

const JsonataEditorv2 = ({ fieldId, readOnly, schemaBindings, isLoading }) => {
  const [monaco, setMonaco] = useState(null);
  const [editor, setEditor] = useState(null);

  // must be unique to keep schema bindings separate from each other
  // (if we just used `jsonata` for all instances of this editor, property suggestor would include union of all registered schema bindings)
  // this is rather inefficient; the language is defined by its schema bindings, so we could share this between multiple editors
  // (e.g. by generating the language ID as a function of schemaBindings - const langId = `jsonata-${md5(JSON.stringify(schemaBindings))}`;)
  // but then this results in multiple identical suggestions being made
  // and, although we clean up the registered completionItemProvider and tokensProvider on unmount, there is no way (that I can see anyway)
  // of deregistering the language as a whole - so there's a memory leak here
  // there might be a better way of doing this - i.e. is there some way we register a single global "jsonata" language with a single completionItem provider
  // and pass in dynamic schema bindings to the provider local to this instance of the editor?
  const [langId] = useState(() => uuidv4());
  const [isLanguageRegistered, setLanguageRegistered] = useState(false);
  useEffect(() => {
    if (!monaco || !langId) {
      return;
    }

    // console.log(`[${langId}]: registering language`)
    monaco.languages.register({ id: langId });
    setLanguageRegistered(true);
  }, [monaco, langId]);

  /**
   * Initialise a dedicated web worker to perform JSONATA
   * parsing, validation and property suggestion operations in the background
   */
  const [worker, setWorker] = useState(undefined);
  useEffect(() => {
    if (!langId) {
      return;
    }
    const theWorker = new Worker();
    setWorker(theWorker);
    // console.log('sending init message to worker');
    theWorker.postMessage({
      action: 'init',
      langId,
      schemaBindings,
    });
    // eslint-disable-next-line consistent-return
    return () => {
      // console.log('Terminating worker');
      theWorker.terminate();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [langId]);

  /**
   * Register a callback with the worker that will
   * add markers to monaco state when a response is received from the worker
   */
  useEffect(() => {
    if (!langId || !worker || !monaco || !editor) {
      return;
    }
    // console.log(`${langId}: Adding marker callback to worker`);
    worker.addEventListener('message', e => {
      if (e.data.action === 'parse') {
        // console.log(`${langId}: parse`)
        monaco.editor.setModelMarkers(editor.getModel(), langId, e.data.markers);
      }
    });
  }, [worker, monaco, editor, langId]);

  /**
   * Request a parse operation from the worker if the
   * expression hasn't changed for a (short) while
   * NOTE: we are using the non-debounced value coming straight out of MonacoEditorFormik's onChange callback
   * This is to keep the parse debouncing built into the jsonata.worker.js independent of the debouncing
   * performed on the formik value from field.value.
   * (if we were to parse in response to the debounced field.value, the "loading..." status for property sugggestions
   * would fail to show in some cases -- but we don't want to skip debouncing our formik sync as this makes things laggy)
   */
  const [field] = useField({ name: fieldId });
  const [value, setValue] = useState(field.value);
  useEffect(() => {
    if (!worker || !value) {
      return;
    }
    worker.postMessage({
      action: 'parse',
      expr: value,
    });
  }, [worker, value]);

  /**
   * Register a completion item provider that queries the web worker
   */
  useEffect(() => {
    if (!langId || !worker || !monaco || !langId || !isLanguageRegistered) {
      return;
    }
    // console.log(`${langId}: registering completion item provider`);

    /** @type {Map<string, {resolve: ()=>void, reject: ()=>void}>} */
    const messageMap = new Map();

    worker.addEventListener('message', e => {
      if (e.data.action !== 'suggest') {
        return;
      }
      const promise = messageMap.get(e.data.id);
      if (!promise) {
        return;
      }
      messageMap.delete(e.data.id);
      const {
        data: { suggestions },
      } = e;
      const mappedSuggestions = suggestions.map(suggestion => ({
        label: suggestion,
        kind: monaco.languages.CompletionItemKind.Field,
        documentation: 'Suggested property',
        insertText: suggestion,
        commitCharacters: ['.'],
        filterText: suggestion,
      }));
      // console.log(`[${langId}]: suggest (${e.data.id}) - `, mappedSuggestions);
      promise.resolve({ suggestions: mappedSuggestions });
    });

    const completer = monaco.languages.registerCompletionItemProvider(langId, {
      provideCompletionItems: (model, position) => {
        const id = uuidv4();
        const promise = new Promise((resolve, reject) => {
          messageMap.set(id, { resolve, reject });
        });
        worker.postMessage({
          action: 'suggest',
          id,
          cursorPosition: model.getValueInRange({
            startLineNumber: 1,
            startColumn: 1,
            endLineNumber: position.lineNumber,
            endColumn: position.column,
          }).length,
        });
        return promise;
        // .then(r => { console.log('.... ', r); return r;});
      },
      resolveCompletionItem: (model__, position__, item, token__) => item,
    });
    // eslint-disable-next-line consistent-return
    return () => {
      // console.log(`${langId}: Deregistering completion item provider`);
      completer.dispose();
    };
  }, [worker, monaco, langId, isLanguageRegistered]);

  // register a tokens provider, that will be used in conjunction with the jsonata theme
  // to colourise keywords
  useEffect(() => {
    if (!langId || !monaco || !isLanguageRegistered) {
      return;
    }
    const tokensProvider = monaco.languages.setMonarchTokensProvider(langId, {
      tokenizer: {
        root: [
          [/\$event/, 'event-schema-var-ref'],
          [/\$state/, 'state-schema-var-ref'],
          [/\$instance/, 'instance-schema-var-ref'],
        ],
      },
    });

    // eslint-disable-next-line consistent-return
    return () => {
      // console.log(`${langId}: Deregistering tokensProvider`);
      tokensProvider.dispose();
    };
  }, [monaco, langId, isLanguageRegistered]);

  // theme can be global since it is independent of schema
  const themeId = 'jsonata';
  useEffect(() => {
    if (!themeId || !monaco) {
      return;
    }

    // TODO: only define theme if not already defined; theme ID is global, this only has to happen once
    monaco.editor.defineTheme(themeId, {
      base: 'vs',
      inherit: false,
      rules: [
        { token: 'event-schema-var-ref', foreground: '0000CC' },
        { token: 'state-schema-var-ref', foreground: '00AA00' },
        { token: 'instance-schema-var-ref', foreground: 'AA00CC' },
      ],
    });
    monaco.editor.setTheme(themeId);
  }, [monaco, themeId]);

  return (
    <MonacoEditorFormik
      width="100%"
      height="60px"
      fieldId={fieldId}
      options={{
        readOnly,
        minimap: {
          enabled: false,
        },
        lineNumbers: false,
        lineNumbersMinChars: 0,
        selectOnLineNumbers: false,
        folding: false,
        foldingHighlight: false,
        quickSuggestions: false,
        suggest: {
          showWords: false,
        },
      }}
      // we do the parse debouncing in the web worker
      syncToFormikDebounceMillis={500}
      language={langId}
      editorWillMount={m => {
        setMonaco(m);
      }}
      editorDidMount={e => {
        setEditor(e);
      }}
      onChange={newValue => {
        setValue(newValue);
      }}
      // auto-transformation between empty string <-> undefined broke the "Add Mapping +" button in the MappingEditor
      // (which initialises an expression as the empty string)
      // not sure why I thought this auto-transformation was necessary, so disabled it for now
      // formikModelToString={(m) => isNil(m) ? '' : m} // interepet undefined expressions as the empty string
      // stringToFormikModel={(s) => isNil(s) ? undefined : s.trim().length === 0 ? undefined : s} // pass back expressions that are just whitespace as undefined
      formikModelToString={m => m}
      stringToFormikModel={s => s}
      isLoading={isLoading}
    />
  );
};

JsonataEditorv2.propTypes = propTypes;
JsonataEditorv2.defaultProps = defaultProps;

export default injectIntl(JsonataEditorv2);
